import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Fab,
  Collapse,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../services/api';
import chatService from '../services/chatService';

const ALINE_FOTO = '/assets/aline_foto.png';

function ChatbotAline({ primeiroAcesso, onClose }) {
  const theme = useTheme();
  const [open, setOpen] = useState(primeiroAcesso);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [tipoPessoa, setTipoPessoa] = useState(null);
  const [freeMessageCount, setFreeMessageCount] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [userPlan, setUserPlan] = useState('FREE');
  const [userId, setUserId] = useState(null);
  const FREE_LIMIT = 6;
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Busca usuário do localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      setUserId(user.id);
      setUserPlan(user.plano || 'FREE');
    }
    // Busca status de bloqueio e histórico de mensagens
    if (user && user.id) {
      (async () => {
        try {
          const bloqueio = await api.get(`/chatbot/blocked/${user.id}`);
          if (bloqueio.bloqueado) {
            setShowUpgrade(true);
            // Garante que a mensagem explicativa do bloqueio apareça sempre
            setMessages(prev => {
              // Se já existe a mensagem de bloqueio, não adiciona novamente
              const jaTemMsg = prev.some(m => m.sender === 'bot' && m.text && m.text.includes('Limite de mensagens'));
              if (jaTemMsg) {
                return prev;
              }
              return [
                ...prev,
                {
                  id: Date.now() + 100,
                  sender: 'bot',
                  text: bloqueio.message || 'Limite de mensagens atingido! Para continuar conversando com a Aline, é necessário assinar o plano Pro. Clique no botão abaixo para saber mais sobre a assinatura.',
                  timestamp: new Date()
                }
              ];
            });
          } else {
          }
          const historico = await api.get(`/chatbot/messages/${user.id}`);
          if (historico && historico.length > 0) {
            // Garante que cada mensagem tenha o campo 'text'
            setMessages(historico.map(m => ({
              ...m,
              text: m.text || m.mensagem || ''
            })));
            setFreeMessageCount(historico.filter(m => m.sender === 'user').length);
            setConversationStarted(true);
          } else if (primeiroAcesso && !conversationStarted) {
            setConversationStarted(true);
            setLoading(true);
            // Gera o insight sob demanda — só quando o usuário realmente abre o chat
            const insight = await chatService.getInsights();
            setLoading(false);
            setMessages([
              {
                id: 1,
                sender: 'bot',
                text: insight?.message || 'Olá! 👋 Eu sou a Aline, sua assistente virtual!',
                timestamp: new Date()
              }
            ]);
          }
        } catch (err) {
          if (primeiroAcesso && !conversationStarted) {
            setLoading(false);
            setConversationStarted(true);
            setMessages([
              {
                id: 1,
                sender: 'bot',
                text: 'Olá! 👋 Eu sou a Aline, sua assistente virtual!',
                timestamp: new Date()
              }
            ]);
          }
        }
      })();
    }
  }, [primeiroAcesso, conversationStarted]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    // Validação para plano FREE
    if (userPlan === 'FREE' && freeMessageCount >= FREE_LIMIT) {
      setShowUpgrade(true);
      return;
    }

    const userMessage = {
      id: Date.now(),
      user_id: userId,
      sender: 'user',
      mensagem: inputMessage,
      text: inputMessage,
      timestamp: new Date(),
      contador_mensagens: freeMessageCount + 1,
      bloqueado: (userPlan === 'FREE' && freeMessageCount + 1 >= FREE_LIMIT)
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    if (userPlan === 'FREE') {
      setFreeMessageCount(count => count + 1);
    }
    // Salva mensagem no backend
    try {
      await api.post('/chatbot/message', userMessage);
      if (userMessage.bloqueado) setShowUpgrade(true);
    } catch (err) {
      // erro ao salvar mensagem
    }

    // Detecta se a resposta contém valores de faturamento/despesas
    const regexFaturamento = /(faturamento|faturamento médio|faturamento mensal|faturo|receita)\s*[:=]?\s*(R?\$?\s?\d+[\.,]?\d*)/i;
    const regexDespesas = /(despesas|despesas médias|despesa mensal|gasto|custo|aluguel)\s*[:=]?\s*(R?\$?\s?\d+[\.,]?\d*)/i;
    const matchFaturamento = inputMessage.match(regexFaturamento);
    const matchDespesas = inputMessage.match(regexDespesas);

    if (matchFaturamento || matchDespesas) {
      // Removido cálculo e mensagens automáticas de imposto do frontend. Apenas salva dados no backend.
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const faturamento = matchFaturamento ? parseFloat(matchFaturamento[2].replace(/[^\d\.,]/g, '').replace(',', '.')) : 0;
        const despesas = matchDespesas ? parseFloat(matchDespesas[2].replace(/[^\d\.,]/g, '').replace(',', '.')) : 0;
        const payload = {
          faturamentoMedio: faturamento,
          despesasMedias: despesas,
          estruturaAtual: tipoPessoa || (user && user.tipoPessoa) || 'PF',
          userId: user ? user.id : undefined,
          periodo: { mes: new Date().getMonth() + 1, ano: new Date().getFullYear() }
        };
        await api.post('/analise/tributaria', payload);
      } catch (err) {
        setMessages(prev => [...prev, {
          id: Date.now() + 6,
          sender: 'bot',
          text: 'Não consegui salvar suas informações. Por favor, tente novamente ou entre em contato com o suporte.',
          timestamp: new Date()
        }]);
      }
    }

    try {
      // Detecta interesse em abrir CNPJ
      const interesseCNPJ = /abrir\s*(um)?\s*cnpj|quero\s*cnpj|abrir empresa|abrir\s*me/i.test(inputMessage);
      if (interesseCNPJ) {
        // Mensagens curtas e simples sobre impostos para dentista
        setMessages(prev => [
          ...prev,
          {
            id: Date.now() + 10,
            sender: 'bot',
            text: 'Dentista não pode ser MEI, ok? O mais comum é abrir como ME no Simples Nacional.',
            timestamp: new Date()
          },
          {
            id: Date.now() + 11,
            sender: 'bot',
            text: 'No Simples Nacional, você paga imposto sobre o faturamento da clínica. O valor depende do quanto você fatura por mês.',
            timestamp: new Date()
          },
          {
            id: Date.now() + 12,
            sender: 'bot',
            text: 'Se quiser, posso te mostrar uma simulação de quanto seria o imposto para sua clínica. Quer ver?',
            timestamp: new Date()
          }
        ]);
        setLoading(false);
        return;
      }
    // Detecta se o usuário pede ajuda ou demonstra dúvida após explicação do CNPJ
    const pedirAjudaCNPJ = /ajuda|contabilidade|contador|não sei|dúvida|como faço|me explica|me ajudar/i.test(inputMessage);
    if (pedirAjudaCNPJ && messages.some(m => m.text.includes('contabilidade especializada'))) {
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 20,
          sender: 'bot',
          text: 'Nós somos a única contabilidade 100% especializada em odontologia, atendendo exclusivamente dentistas.',
          timestamp: new Date()
        },
        {
          id: Date.now() + 21,
          sender: 'bot',
          text: 'Já ajudamos mais de 400 clínicas em todo o Brasil! Se quiser conversar, nosso WhatsApp é 55 11 95395-6778 ou acesse https://visiontax.com.br/',
          timestamp: new Date()
        }
      ]);
      setLoading(false);
      return;
    }

      // Chama a API normalmente
      const response = await api.post('/chat/message', {
        message: inputMessage,
        conversationHistory: messages
      });

      // Quebra respostas longas em mensagens curtas
      const botTexts = response.message.split(/(?<=[.!?])\s{2,}|\n{2,}/).filter(t => t.trim().length > 0);
      const botMessages = botTexts.map((text, idx) => ({
        id: Date.now() + 1 + idx,
        sender: 'bot',
        text: text.trim(),
        timestamp: new Date()
      }));
      setMessages(prev => [...prev, ...botMessages]);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        text: 'Desculpe, tive um problema para processar sua mensagem. Pode tentar novamente?',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  // Buscar tipo de pessoa do usuário ao iniciar
  useEffect(() => {
    async function fetchTipoPessoa() {
      try {
        // Exemplo: buscar do backend ou localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        if (user && user.tipoPessoa) {
          setTipoPessoa(user.tipoPessoa);
        } else {
          setTipoPessoa('PF'); // fallback
        }
      } catch (err) {
        setTipoPessoa('PF');
      }
    }
    fetchTipoPessoa();
  }, []);

  useEffect(() => {
    if (primeiroAcesso && !conversationStarted && tipoPessoa) {
      // Mensagem inicial personalizada
      let tipoLabel = '';
      if (tipoPessoa === 'PF') tipoLabel = 'Pessoa Física';
      else if (tipoPessoa === 'PJ') tipoLabel = 'Pessoa Jurídica';
      else tipoLabel = 'Híbrido';
      setTimeout(() => {
        setMessages([
          {
            id: 1,
            sender: 'bot',
            text: `Olá! 👋 Eu sou a Aline, sua assistente virtual!`,
            timestamp: new Date()
          },
          {
            id: 2,
            sender: 'bot',
            text: `Vi que você atende como ${tipoLabel}. Posso te ajudar com dúvidas sobre atuar como Pessoa Física.`,
            timestamp: new Date()
          },
          {
            id: 3,
            sender: 'bot',
            text: 'Você sente que está pagando muito imposto ou tem alguma dúvida sobre o modo PF? 😊',
            timestamp: new Date()
          }
        ]);
        setConversationStarted(true);
      }, 1000);
    }
  }, [primeiroAcesso, conversationStarted, tipoPessoa]);

  const handleClose = async () => {
    setOpen(false);
    // Marca que não é mais o primeiro acesso
    try {
      await api.post('/user/mark-not-first-access');
    } catch (error) {
      console.error('Erro ao marcar primeiro acesso:', error);
    }
    if (onClose) onClose();
  };

  if (!open) {
    return (
      <Fab
        aria-label="chat"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 64,
          height: 64,
          padding: 0,
          overflow: 'hidden',
          background: 'transparent',
          boxShadow: '0 4px 20px rgba(102,126,234,0.5)',
          '&:hover': {
            background: 'transparent',
            boxShadow: '0 6px 24px rgba(102,126,234,0.7)',
          }
        }}
      >
        <img
          src={ALINE_FOTO}
          alt="Aline"
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
        />
      </Fab>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 380,
        height: 600,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        zIndex: 1300,
        backgroundColor: theme.palette.background.paper
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={ALINE_FOTO}
            alt="Aline"
            sx={{
              width: 45,
              height: 45,
              border: '2px solid white'
            }}
          />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Aline
            </Typography>
            <Typography variant="caption">
              Assistente Virtual
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}
      >
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            {message.sender === 'bot' && (
              <Avatar
                src={ALINE_FOTO}
                alt="Aline"
                sx={{
                  width: 32,
                  height: 32,
                  border: '1px solid #667eea'
                }}
              />
            )}
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                maxWidth: '75%',
                bgcolor: message.sender === 'user' ? theme.palette.primary.main : theme.palette.background.paper,
                color: message.sender === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                borderRadius: 2,
                borderTopLeftRadius: message.sender === 'bot' ? 0 : 16,
                borderTopRightRadius: message.sender === 'user' ? 0 : 16
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.text}
              </Typography>
            </Paper>
          </Box>
        ))}
        {showUpgrade && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 2 }}>
            <Paper elevation={3} sx={{ bgcolor: '#ffe6e6ff', p: 2, border: '1px solid #ff0000ff', borderRadius: 2 }}>
              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                Limite de mensagens atingido!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.mode === 'dark' ? '#434343ff' : '#333333'
                }}
              >
                Para continuar conversando com a Aline, é necessário assinar o plano Pro.<br />
                Clique no botão abaixo para saber mais sobre a assinatura.
              </Typography>
            </Paper>
            <Box sx={{ mt: 2 }}>
              <Chip
                color="primary"
                label="Assinar plano Pro"
                clickable
                onClick={() => window.open('https://visiontax.com.br/assinatura', '_blank')}
                sx={{ fontWeight: 'bold', fontSize: '1rem', p: 2 }}
              />
            </Box>
          </Box>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={ALINE_FOTO}
              alt="Aline"
              sx={{
                width: 32,
                height: 32,
                border: '1px solid #667eea'
              }}
            />
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                borderTopLeftRadius: 0
              }}
            >
              <CircularProgress size={20} />
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area - só aparece se não estiver bloqueado */}
      {!showUpgrade && (
        <Box
          sx={{
            p: 2,
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 1
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Digite sua mensagem..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading || (userPlan === 'FREE' && freeMessageCount >= FREE_LIMIT)}
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || loading || (userPlan === 'FREE' && freeMessageCount >= FREE_LIMIT)}
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              color: 'white',
              borderRadius: 2,
              '&:hover': {
                background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)',
              },
              '&.Mui-disabled': {
                bgcolor: '#ccc',
                color: '#999'
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
}

export default ChatbotAline;
